import { ApolloClient, InMemoryCache, HttpLink, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { acquireApiToken } from '@/app/core/auth/authToken';
import { loginRequest, msalInstance } from '@/app/core/auth/msalConfig';

// Resolve to an ABSOLUTE URL anchored to the page origin. A bare relative
// value (e.g. "/graphql") can be mis-resolved by some clients/environments; an
// absolute URL guarantees the POST lands on the deployed backend via Caddy.
const rawGraphqlEndpoint =
  (import.meta.env?.VITE_GRAPHQL_ENDPOINT as string | undefined) || '/graphql';
const graphqlEndpoint = /^https?:\/\//i.test(rawGraphqlEndpoint)
  ? rawGraphqlEndpoint
  : `${typeof window !== 'undefined' ? window.location.origin : ''}${
      rawGraphqlEndpoint.startsWith('/') ? '' : '/'
    }${rawGraphqlEndpoint}`;

const httpLink = new HttpLink({
  uri: graphqlEndpoint,
});

// Attach the B2C access token as a Bearer header. No-op in mock/dev (token is null),
// so the backend's mock-admin chain (security.auth.enabled=false) keeps serving.
const authLink = setContext(async (_, { headers }) => {
  const token = await acquireApiToken();
  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, path }) => {
      console.error(`[GraphQL error]: Message: ${message}, Path: ${path}`);
    });
  }
  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
  }

  // Re-authenticate on 401 / UNAUTHENTICATED when running against real B2C.
  const status = (networkError as { statusCode?: number } | null)?.statusCode;
  const unauthenticated =
    status === 401 ||
    graphQLErrors?.some((e) => e.extensions?.code === 'UNAUTHENTICATED');
  if (unauthenticated && msalInstance && msalInstance.getAllAccounts().length > 0) {
    void msalInstance.acquireTokenRedirect({ ...loginRequest });
  }
});

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        products: { merge: (_, incoming) => incoming },
        warehouses: { merge: (_, incoming) => incoming },
        movements: { merge: (_, incoming) => incoming },
        cycleCounts: { merge: (_, incoming) => incoming },
        lowStockAlerts: { merge: (_, incoming) => incoming },
        vendors: { merge: (_, incoming) => incoming },
        purchaseOrders: { merge: (_, incoming) => incoming },
        receivesForPO: { merge: (_, incoming) => incoming },
        fulfillmentOrders: { merge: (_, incoming) => incoming },
        myFulfillmentQueue: { merge: (_, incoming) => incoming },
      },
    },
    Product: { keyFields: ['id'] },
    Warehouse: { keyFields: ['id'] },
    StockMovement: { keyFields: ['id'] },
    CycleCount: { keyFields: ['id'] },
    Vendor: { keyFields: ['id'] },
    PurchaseOrder: { keyFields: ['id'] },
    PurchaseReceive: { keyFields: ['id'] },
    FulfillmentOrder: { keyFields: ['id'] },
  },
});

export const apolloClient = new ApolloClient({
  link: from([authLink, errorLink, httpLink]),
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});
